import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useCompany } from '../context/CompanyContext';

/**
 * Default branding fallbacks when company settings are missing.
 * These ensure the print quote always renders something reasonable.
 */
const DEFAULT_BRANDING = {
  companyName: 'Your Company',
  tagline: '',
  logoUrl: null,
  address: {
    line1: '',
    city: '',
    state: '',
    zip: '',
  },
  phone: '',
  email: '',
  website: '',
  colors: {
    primary: '#f26b1d',
    secondary: '#0f4c81',
  },
};

/**
 * Hook to fetch company branding settings.
 * 
 * Fetches from: companies/{companyId}/settings/branding
 * 
 * Returns branding object with sensible defaults for missing fields.
 */
export function useCompanyBranding() {
  const { companyId } = useCompany();
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const fetchBranding = async () => {
      try {
        setLoading(true);
        setError(null);

        const brandingRef = doc(db, 'companies', companyId, 'settings', 'branding');
        const brandingSnap = await getDoc(brandingRef);

        if (brandingSnap.exists()) {
          const data = brandingSnap.data();
          // Merge with defaults to ensure all fields exist
          setBranding({
            companyName: data.companyName || DEFAULT_BRANDING.companyName,
            tagline: data.tagline || DEFAULT_BRANDING.tagline,
            logoUrl: data.logoUrl || DEFAULT_BRANDING.logoUrl,
            address: {
              line1: data.address?.line1 || DEFAULT_BRANDING.address.line1,
              city: data.address?.city || DEFAULT_BRANDING.address.city,
              state: data.address?.state || DEFAULT_BRANDING.address.state,
              zip: data.address?.zip || DEFAULT_BRANDING.address.zip,
            },
            phone: data.phone || DEFAULT_BRANDING.phone,
            email: data.email || DEFAULT_BRANDING.email,
            website: data.website || DEFAULT_BRANDING.website,
            colors: {
              primary: data.colors?.primary || DEFAULT_BRANDING.colors.primary,
              secondary: data.colors?.secondary || DEFAULT_BRANDING.colors.secondary,
            },
          });
        } else {
          // No branding document - use defaults
          setBranding(DEFAULT_BRANDING);
        }
      } catch (err) {
        console.error('Failed to fetch company branding:', err);
        setError(err);
        setBranding(DEFAULT_BRANDING);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, [companyId]);

  return { branding, loading, error };
}

/**
 * Format address object into a single-line string.
 */
export function formatAddress(address) {
  if (!address) return '';
  const parts = [];
  if (address.line1) parts.push(address.line1);
  const cityStateZip = [
    address.city,
    address.state,
    address.zip,
  ].filter(Boolean).join(', ').replace(/, ([^,]+)$/, ' $1');
  if (cityStateZip) parts.push(cityStateZip);
  return parts.join(', ');
}

export default useCompanyBranding;
